/**
 * kaged-faq.js
 *
 * Dual-mode FAQ uses a transformed track (not overflow scrollTop).
 * Expanding overflow:auto mid-list zeroed scrollTop and snapped to ABOUT.
 * Offset lives in JS + --faq-offset so mid-list position cannot be wiped.
 *
 * 1) Before nav sticks → one document scroll.
 * 2) After nav sticks → dual:
 *      • over questions → move FAQ track (offset)
 *      • anywhere else → page scroll (offset stays put)
 * 3) Dual only expands when offset is 0 (or convert offset → page scroll first).
 * 4) Category click → pin sticky, then move track to that section.
 */
(() => {
    const initKagedFaq = (root) => {
        if (root.dataset.kagedFaqReady === "true") return;
        root.dataset.kagedFaqReady = "true";

        const searchInput = root.querySelector(".faq-search-input");
        const workspace = root.querySelector(".kaged-faq-workspace");
        let track = root.querySelector(".kaged-faq-workspace-track");
        const emptyEl = root.querySelector(".kaged-faq-empty");
        const subnav = root.querySelector(".subnav-wrapper-section");
        const sentinel = root.querySelector(".kaged-faq-stick-sentinel");
        const groupBlocks = Array.from(root.querySelectorAll(".faq-group-block"));
        const subNavLinks = Array.from(root.querySelectorAll(".kaged-faq-subnav .subnav-link"));
        const rows = root.querySelectorAll(".faq-row");

        // Back-compat if theme liquid wasn't re-uploaded yet
        if (workspace && !track) {
            track = document.createElement("div");
            track.className = "kaged-faq-workspace-track";
            while (workspace.firstChild) track.appendChild(workspace.firstChild);
            workspace.appendChild(track);
        }

        let dual = false;
        let offset = 0; // FAQ track translate (px) — source of truth
        let navigating = false;
        let lockActiveId = ""; // keep clicked cell active until navigation finishes
        let offsetAnim = 0;
        let offsetDone = null; // pending setOffset completion callback

        const siteHeaderOffset = () => {
            const candidates = document.querySelectorAll(
                "sticky-header, .shopify-section-header-sticky, .header-wrapper, #shopify-section-header, header.header, .header"
            );
            let bottom = 0;
            candidates.forEach((el) => {
                const style = window.getComputedStyle(el);
                const fixedish =
                    style.position === "fixed" ||
                    style.position === "sticky" ||
                    el.tagName === "STICKY-HEADER";
                if (!fixedish) return;
                const r = el.getBoundingClientRect();
                if (r.bottom > bottom && r.top < 8 && r.height < 200) {
                    bottom = r.bottom;
                }
            });
            return Math.max(0, Math.round(bottom));
        };

        const applyStickyTop = () => {
            const t = siteHeaderOffset();
            root.style.setProperty("--faq-sticky-top", `${t}px`);
            return t;
        };

        const stickyTop = () => {
            const n = parseFloat(getComputedStyle(root).getPropertyValue("--faq-sticky-top"));
            return Number.isFinite(n) ? n : 0;
        };

        const measureSubnav = () => {
            if (!subnav) return 0;
            const h = subnav.offsetHeight;
            root.style.setProperty("--faq-subnav-height", `${h}px`);
            return h;
        };

        const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;

        const pinY = () => (subnav ? Math.max(0, docTop(subnav) - stickyTop()) : 0);

        const needsDual = () => {
            if (!workspace || !track || !subnav) return false;
            measureSubnav();
            applyStickyTop();
            const avail = window.innerHeight - stickyTop() - subnav.offsetHeight - 16;
            // Use track height (full content) not clipped workspace
            return track.scrollHeight > avail + 24;
        };

        const maxOffset = () => {
            if (!workspace || !track) return 0;
            if (!dual) return Math.max(0, track.scrollHeight - window.innerHeight * 0.5);
            return Math.max(0, track.scrollHeight - workspace.clientHeight);
        };

        const firstGroupId = () => {
            const g = groupBlocks.find((b) => b.style.display !== "none");
            return g ? g.id : "";
        };

        const setActive = (id) => {
            if (!id) return;
            subNavLinks.forEach((link) => {
                link.classList.toggle("active", link.getAttribute("href") === `#${id}`);
            });
        };

        const paintOffset = () => {
            if (!workspace) return;
            const clamped = Math.max(0, Math.min(maxOffset(), offset));
            offset = clamped;
            workspace.style.setProperty("--faq-offset", `${clamped}px`);
        };

        /**
         * Layout Y of el relative to the track top (px).
         * Both elems share translateY(-offset), so getBoundingClientRect
         * difference is already transform-invariant — never add offset again.
         */
        const layoutYInTrack = (el) => {
            if (!track || !el) return 0;
            return el.getBoundingClientRect().top - track.getBoundingClientRect().top;
        };

        const setOffset = (value, { smooth = false, onDone } = {}) => {
            if (!workspace) return;

            if (offsetAnim) {
                cancelAnimationFrame(offsetAnim);
                offsetAnim = 0;
            }
            // Drop any prior completion callback (new nav supersedes it)
            offsetDone = typeof onDone === "function" ? onDone : null;

            const target = Math.max(0, Math.min(maxOffset(), value));
            const finish = () => {
                const cb = offsetDone;
                offsetDone = null;
                if (typeof cb === "function") cb();
            };

            if (!smooth || !dual) {
                offset = target;
                paintOffset();
                updateActiveNav();
                finish();
                return;
            }

            const start = offset;
            const delta = target - start;
            if (Math.abs(delta) < 1) {
                offset = target;
                paintOffset();
                updateActiveNav();
                finish();
                return;
            }

            const duration = 380;
            const t0 = performance.now();
            const ease = (t) => 1 - Math.pow(1 - t, 3);

            const step = (now) => {
                const t = Math.min(1, (now - t0) / duration);
                offset = start + delta * ease(t);
                paintOffset();
                // Hold the clicked cell while animating so reverse nav doesn't flicker
                if (lockActiveId) setActive(lockActiveId);
                else updateActiveNav();
                if (t < 1) offsetAnim = requestAnimationFrame(step);
                else {
                    offset = target;
                    paintOffset();
                    offsetAnim = 0;
                    finish();
                }
            };
            offsetAnim = requestAnimationFrame(step);
        };

        /**
         * Document lock-line → how far into the track that content is.
         * Used when entering dual so the visible category is not replaced by ABOUT.
         */
        const contentOffsetAtLockLine = () => {
            if (!workspace || !track || !subnav) return 0;
            measureSubnav();
            const line = stickyTop() + subnav.offsetHeight;
            // Content Y of track origin in viewport, accounting for current transform
            const trackTop = track.getBoundingClientRect().top + offset;
            return Math.max(0, line - trackTop);
        };

        const updateActiveNav = () => {
            if (!workspace) return;

            // Don't fight a deliberate category click mid-scroll
            if (navigating && lockActiveId) {
                setActive(lockActiveId);
                return;
            }

            const threshold = dual
                ? workspace.getBoundingClientRect().top + 28
                : stickyTop() + (subnav ? subnav.offsetHeight : 0) + 28;

            let id = "";
            groupBlocks.forEach((block) => {
                if (block.style.display === "none") return;
                if (block.getBoundingClientRect().top <= threshold) id = block.id;
            });

            setActive(id || firstGroupId());
        };

        const isSticky = () => {
            if (!sentinel) return false;
            return sentinel.getBoundingClientRect().bottom <= stickyTop() + 1;
        };

        const enableDual = () => {
            if (!workspace || !track || !needsDual()) return false;

            const wasDual = dual;
            applyStickyTop();
            measureSubnav();

            // Capture the category under the nav before clipping
            const restore = wasDual ? offset : Math.max(offset, contentOffsetAtLockLine());

            root.classList.add("is-dual-scroll");
            dual = true;
            measureSubnav();
            setOffset(restore);
            return true;
        };

        /**
         * Leaving dual mid-list: fold current offset into the page scroll so the
         * same questions stay under the nav (never jump to ABOUT).
         */
        const disableDual = ({ force = false } = {}) => {
            if (!workspace || !track || !dual) return false;

            const wasOffset = offset;
            const screenTop = workspace.getBoundingClientRect().top;

            if (!force && wasOffset > 0.5) {
                // Convert nested offset → page position, then expand
                root.classList.remove("is-dual-scroll");
                dual = false;
                offset = 0;
                paintOffset();

                // Content that sat at track offset was at screenTop;
                // after expand it's at workspaceTop + wasOffset in document terms.
                requestAnimationFrame(() => {
                    const contentDocY =
                        workspace.getBoundingClientRect().top + window.scrollY + wasOffset;
                    window.scrollTo({ top: Math.max(0, contentDocY - screenTop), left: 0, behavior: "auto" });
                    updateActiveNav();
                });
                return true;
            }

            root.classList.remove("is-dual-scroll");
            dual = false;
            offset = 0;
            paintOffset();
            updateActiveNav();
            return true;
        };

        const chainPageScroll = (deltaY) => {
            window.scrollBy({ top: deltaY, left: 0, behavior: "auto" });

            // At top of FAQ track: expand only when sticky released and offset is 0
            if (dual && offset <= 0.5 && !isSticky()) {
                disableDual({ force: true });
            }
        };

        const syncDual = () => {
            if (!workspace || !track || navigating) return;

            applyStickyTop();
            const sticky = isSticky();

            if (sticky && needsDual()) {
                if (!dual) enableDual();
                else paintOffset(); // re-clamp if layout changed
            } else if (!sticky && dual) {
                // Stay dual while mid-list so transform keeps showing the same spot.
                // Only auto-expand when offset is already 0 (safe).
                if (offset <= 0.5) {
                    disableDual({ force: true });
                } else {
                    paintOffset();
                }
            }

            updateActiveNav();
        };

        const scrollWorkspaceTo = (target, smooth = true, onDone) => {
            if (!workspace || !track || !target) {
                if (typeof onDone === "function") onDone();
                return;
            }

            // Transform-invariant Y of the section within the track.
            // DO NOT add `offset` here — the rect diff already ignores translateY(-offset).
            // Adding it broke reverse category clicks (destination ≈ current offset).
            const top = Math.max(0, layoutYInTrack(target) - 16);
            setOffset(top, { smooth, onDone });
        };

        const navigateToCategory = (target) => {
            if (!target || !workspace) return;

            navigating = true;
            lockActiveId = target.id || "";
            if (lockActiveId) setActive(lockActiveId);

            applyStickyTop();
            measureSubnav();

            const pin = pinY();
            const needsPin = !isSticky() || Math.abs(window.scrollY - pin) > 6;

            const endNavigate = () => {
                navigating = false;
                const id = lockActiveId;
                lockActiveId = "";
                if (id) setActive(id);
                else updateActiveNav();
            };

            const afterPinned = () => {
                enableDual();
                window.scrollTo({ top: pinY(), left: 0, behavior: "auto" });

                // Dual maxOffset depends on clipped workspace — measure after clip paints
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scrollWorkspaceTo(target, true, endNavigate);
                    });
                });
            };

            if (needsPin) {
                window.scrollTo({ top: pin, left: 0, behavior: "smooth" });

                let settled = false;
                const settle = () => {
                    if (settled) return;
                    if (isSticky() || Math.abs(window.scrollY - pinY()) <= 10) {
                        settled = true;
                        window.removeEventListener("scroll", onScroll);
                        afterPinned();
                    }
                };
                const onScroll = () => settle();
                window.addEventListener("scroll", onScroll, { passive: true });
                setTimeout(settle, 700);
            } else {
                afterPinned();
            }
        };

        applyStickyTop();
        measureSubnav();
        setActive(firstGroupId());

        window.addEventListener(
            "resize",
            () => {
                applyStickyTop();
                measureSubnav();
                if (dual) paintOffset();
                syncDual();
            },
            { passive: true }
        );

        window.addEventListener("scroll", syncDual, { passive: true });

        rows.forEach((row) => {
            const summary = row.querySelector(".faq-question");
            const text = summary && summary.querySelector(".faq-text-content");
            if (text) summary.setAttribute("data-original-text", text.textContent.trim());
        });

        const smoothClose = (row) => {
            if (!row.hasAttribute("open") || row.classList.contains("is-closing")) return;
            row.classList.add("is-closing");
            setTimeout(() => {
                row.removeAttribute("open");
                row.classList.remove("is-closing");
            }, 300);
        };

        if (searchInput) {
            searchInput.addEventListener("input", (e) => {
                const query = e.target.value.trim();
                const lower = query.toLowerCase();
                if (workspace) workspace.classList.toggle("is-searching", lower.length > 0);

                let totalVisible = 0;

                groupBlocks.forEach((block) => {
                    const blockRows = block.querySelectorAll(".faq-row");
                    let hasVisible = false;

                    blockRows.forEach((row, index) => {
                        const summary = row.querySelector(".faq-question");
                        const original = summary.getAttribute("data-original-text") || "";
                        const arrow = summary.querySelector(".arrow");
                        const matches = !lower || original.toLowerCase().includes(lower);

                        row.style.display = matches ? "block" : "none";
                        if (matches) {
                            hasVisible = true;
                            if (lower) totalVisible += 1;
                        }

                        if (lower) {
                            if (matches) row.setAttribute("open", "");
                        } else if (index === 0) {
                            row.setAttribute("open", "");
                        } else {
                            row.removeAttribute("open");
                        }

                        const display =
                            matches && query
                                ? original.replace(
                                      new RegExp(
                                          `(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})`,
                                          "gi"
                                      ),
                                      "<strong>$1</strong>"
                                  )
                                : original;

                        summary.innerHTML = `<span class="faq-text-content">${display}</span>`;
                        if (arrow) summary.appendChild(arrow);
                    });

                    block.style.display = hasVisible || !lower ? "block" : "none";
                });

                if (emptyEl) {
                    emptyEl.classList.toggle("is-visible", lower.length > 0 && totalVisible === 0);
                }

                if (dual) paintOffset();
                updateActiveNav();
            });
        }

        subNavLinks.forEach((link) => {
            link.addEventListener("click", (e) => {
                e.preventDefault();

                const href = link.getAttribute("href");
                if (!href || href === "#") return;

                const target = root.querySelector(href);
                if (!target) return;

                // Immediate visual feedback (including reverse direction)
                subNavLinks.forEach((item) => item.classList.toggle("active", item === link));
                navigateToCategory(target);
            });
        });

        rows.forEach((currentRow) => {
            const summary = currentRow.querySelector(".faq-question");
            summary.addEventListener("click", (e) => {
                e.preventDefault();
                const group = currentRow.closest(".faq-group-block");

                if (currentRow.hasAttribute("open") && !currentRow.classList.contains("is-closing")) {
                    smoothClose(currentRow);
                } else if (!currentRow.hasAttribute("open")) {
                    group.querySelectorAll(".faq-row").forEach((other) => {
                        if (other !== currentRow) smoothClose(other);
                    });
                    currentRow.setAttribute("open", "");
                    currentRow.classList.add("is-opening");
                    requestAnimationFrame(() => {
                        currentRow.classList.remove("is-opening");
                        if (dual) paintOffset();
                    });
                }
            });
        });

        if (workspace && track) {
            // Own ALL wheel interaction while dual — never use overflow scroll
            workspace.addEventListener(
                "wheel",
                (e) => {
                    if (!dual) return;

                    const max = maxOffset();
                    if (max <= 0) return;

                    const up = e.deltaY < 0;
                    const down = e.deltaY > 0;
                    const atTop = offset <= 0.5;
                    const atBottom = offset >= max - 0.5;

                    e.preventDefault();

                    if ((atBottom && down) || (atTop && up)) {
                        chainPageScroll(e.deltaY);
                        return;
                    }

                    setOffset(offset + e.deltaY);
                },
                { passive: false }
            );

            let touchY = 0;
            workspace.addEventListener(
                "touchstart",
                (e) => {
                    touchY = e.touches[0] ? e.touches[0].clientY : 0;
                },
                { passive: true }
            );

            workspace.addEventListener(
                "touchmove",
                (e) => {
                    if (!dual || !e.touches[0]) return;

                    const max = maxOffset();
                    if (max <= 0) return;

                    const y = e.touches[0].clientY;
                    const dy = touchY - y;
                    touchY = y;

                    const atTop = offset <= 0.5;
                    const atBottom = offset >= max - 0.5;

                    if ((atBottom && dy > 0) || (atTop && dy < 0)) {
                        chainPageScroll(dy);
                        return;
                    }

                    if (e.cancelable) e.preventDefault();
                    setOffset(offset + dy);
                },
                { passive: false }
            );
        }

        syncDual();
        updateActiveNav();
    };

    const boot = () => {
        document.querySelectorAll(".kaged-faq-root").forEach(initKagedFaq);
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }

    document.addEventListener("shopify:section:load", (event) => {
        const root = event.target.querySelector(".kaged-faq-root");
        if (root) {
            root.dataset.kagedFaqReady = "false";
            initKagedFaq(root);
        }
    });
})();
